import React from 'react';
import {
  Button,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  ProgressBar,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { StatusPill } from '../../../theme/components/StatusPill';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideDrawer } from '../../../theme/components/GuideDrawer';
import { GuideHelpText } from '../../../theme/components/GuideField';
import {
  ArrowSync16Regular,
  Checkmark16Regular,
  CheckmarkCircle16Regular,
  Clock16Regular,
  ErrorCircle16Regular,
  Prohibited16Regular,
  Warning16Regular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { formatFileSize } from '../../../functions/AzureFileUpload.Functions';
import { validateExplorerUploadFile } from '../../../functions/StorageManagement.Functions';
import { getUploadErrorMessage } from '../../Commons/AzureFileUploadDrawer/AzureFileUploadDrawer.Functions';

// Design guide: Fluent tokens only, status as outline pill with icon + text, MessageBar for errors.
const useStyles = makeStyles({
  list: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalS,
    listStyleType: 'none',
    ...shorthands.margin(0),
    ...shorthands.padding(0),
  },
  item: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXS,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
  },
  itemHeader: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  name: {
    flexGrow: 1,
    minWidth: 0,
    overflowX: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  size: {
    color: tokens.colorNeutralForeground2,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },
  validation: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXS,
    fontSize: tokens.fontSizeBase200,
    color: 'var(--ichtys-error)',
  },
});

export type ExplorerUploadStatus = 'invalid' | 'pending' | 'uploading' | 'uploaded' | 'error';

/** Registration in the owner service after uploading into an entity folder (spec 005, US7). */
export type ExplorerRegisterStatus = 'registering' | 'registered' | 'failed';

interface UploadRow {
  id: string;
  file: File;
  status: ExplorerUploadStatus;
  progress: number;
  /** i18n key (invalid) */
  validation?: string;
  /** API / Azure detail (error) */
  error?: string;
  /** Blob path returned by the upload (used to register / retry the register) */
  blobPath?: string;
  /** Only for files uploaded into an entity folder */
  registration?: ExplorerRegisterStatus;
  /** API detail of the failed register */
  registerError?: string;
}

export interface ExplorerUploadBatch {
  id: number;
  files: File[];
}

export interface ExplorerUploadPanelProps {
  open: boolean;
  /** Friendly name of the destination folder */
  folderLabel: string;
  /** Every new batch (picker or drop) is appended and uploaded right away */
  batch: ExplorerUploadBatch | undefined;
  allowedExtensions: string[] | null;
  /** Uploads one file into the folder; rejects with ErrorType */
  uploadFile: (file: File, onProgress: (percent: number) => void) => Promise<string>;
  /** Called after each run that uploaded at least one file (refresh the folder) */
  onUploaded: (count: number) => void;
  onDismiss: () => void;
  /**
   * The uploaded blob lives in an entity folder that an owner service registers (spec 005, US7).
   * Undefined (or false) = the upload ends there.
   */
  isRegistrable?: (blobPath: string) => boolean;
  /** Registers the uploaded blob in its owner service; rejects with ErrorType. Never fails the upload. */
  registerFile?: (blobPath: string) => Promise<unknown>;
}

const badge: Record<ExplorerUploadStatus, { color: 'informative' | 'brand' | 'success' | 'danger'; icon: JSX.Element }> = {
  invalid: { color: 'danger', icon: <Prohibited16Regular /> },
  pending: { color: 'informative', icon: <Clock16Regular /> },
  uploading: { color: 'brand', icon: <ArrowSync16Regular /> },
  uploaded: { color: 'success', icon: <Checkmark16Regular /> },
  error: { color: 'danger', icon: <ErrorCircle16Regular /> },
};

const registerBadge: Record<ExplorerRegisterStatus, { color: 'brand' | 'success' | 'warning'; icon: JSX.Element }> = {
  registering: { color: 'brand', icon: <ArrowSync16Regular /> },
  registered: { color: 'success', icon: <CheckmarkCircle16Regular /> },
  failed: { color: 'warning', icon: <Warning16Regular /> },
};

let sequence = 0;

/** Upload drawer (guide size "large", file uploads) with the per-file progress into the current explorer folder. */
export const ExplorerUploadPanel = (props: ExplorerUploadPanelProps) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const [rows, setRows] = React.useState<UploadRow[]>([]);
  const [running, setRunning] = React.useState(false);
  const queue = React.useRef<Promise<void>>(Promise.resolve());
  const mounted = React.useRef(true);
  const lastBatch = React.useRef<number | undefined>(undefined);

  const { uploadFile, onUploaded, allowedExtensions, batch, isRegistrable, registerFile } = props;

  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const update = (id: string, changes: Partial<UploadRow>) => {
    if (!mounted.current) return;
    setRows((current) => current.map((row) => (row.id === id ? { ...row, ...changes } : row)));
  };

  /** Registers an uploaded blob; the upload itself is already done and never undone. */
  const register = React.useCallback(
    async (id: string, blobPath: string) => {
      if (!registerFile) return;
      update(id, { registration: 'registering', registerError: undefined });
      try {
        await registerFile(blobPath);
        update(id, { registration: 'registered' });
      } catch (error: unknown) {
        update(id, { registration: 'failed', registerError: getUploadErrorMessage(error) });
      }
    },
    [registerFile] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const run = React.useCallback(
    (toUpload: UploadRow[]) => {
      if (toUpload.length === 0) return;
      queue.current = queue.current.then(async () => {
        if (mounted.current) setRunning(true);
        let uploaded = 0;
        for (const row of toUpload) {
          update(row.id, { status: 'uploading', progress: 0, error: undefined });
          try {
            const blobPath = await uploadFile(row.file, (percent) => update(row.id, { progress: percent }));
            uploaded++;
            update(row.id, { status: 'uploaded', progress: 100, blobPath });
            if (blobPath && registerFile && isRegistrable?.(blobPath)) await register(row.id, blobPath);
          } catch (error: unknown) {
            update(row.id, { status: 'error', error: getUploadErrorMessage(error) });
          }
        }
        if (!mounted.current) return;
        setRunning(false);
        if (uploaded > 0) onUploaded(uploaded);
      });
    },
    [uploadFile, onUploaded, registerFile, isRegistrable, register] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // A new batch of files: validate (blocked / allowed extensions, size) and upload the valid ones.
  React.useEffect(() => {
    if (!batch || batch.id === lastBatch.current) return;
    lastBatch.current = batch.id;
    const incoming = batch.files.map((file): UploadRow => {
      const validation = validateExplorerUploadFile(file, allowedExtensions);
      return {
        id: `${Date.now()}-${sequence++}`,
        file,
        status: validation ? 'invalid' : 'pending',
        progress: 0,
        validation,
      };
    });
    setRows((current) => [...current, ...incoming]);
    run(incoming.filter((row) => row.status === 'pending'));
  }, [batch, allowedExtensions, run]);

  const close = () => {
    if (running) return;
    setRows([]);
    props.onDismiss();
  };

  return (
    <GuideDrawer
      open={props.open}
      size="large"
      title={t('filesExplorer.upload.panelTitle')}
      onDismiss={close}
      preventDismiss={running}
      closeLabel={t('filesExplorer.actions.close')}
      testId="explorer-upload-panel"
      secondaryAction={{ label: t('filesExplorer.actions.close') }}
    >
      <GuideHelpText>{t('filesExplorer.upload.destination', { folder: props.folderLabel })}</GuideHelpText>
      <ul className={styles.list} aria-live="polite">
        {rows.map((row) => (
          <li key={row.id} className={styles.item} data-testid="explorer-upload-item">
            <div className={styles.itemHeader}>
              <span className={styles.name} title={row.file.name}>
                {row.file.name}
              </span>
              <span className={styles.size}>{formatFileSize(row.file.size)}</span>
              <StatusPill
                color={badge[row.status].color}
                icon={badge[row.status].icon}
                data-testid={`explorer-upload-status-${row.status}`}
              >
                {t(`filesExplorer.upload.status.${row.status}`)}
              </StatusPill>
              {row.registration && (
                <StatusPill
                  color={registerBadge[row.registration].color}
                  icon={registerBadge[row.registration].icon}
                  data-testid={`explorer-register-status-${row.registration}`}
                >
                  {t(`filesExplorer.upload.register.${row.registration}`)}
                </StatusPill>
              )}
            </div>
            {row.status === 'invalid' && row.validation && (
              <span className={styles.validation} role="alert">
                <ErrorCircle16Regular />
                {t(row.validation, { extensions: (allowedExtensions ?? []).join(', ') })}
              </span>
            )}
            {(row.status === 'uploading' || row.status === 'uploaded') && (
              <ProgressBar
                value={row.progress / 100}
                thickness="large"
                color={row.status === 'uploaded' ? 'success' : 'brand'}
                aria-label={t(`filesExplorer.upload.status.${row.status}`)}
              />
            )}
            {row.registration === 'failed' && (
              <GuideMessageBar intent="warning" layout="multiline" data-testid="explorer-register-failed">
                <MessageBarBody>
                  <MessageBarTitle>{t('filesExplorer.upload.register.failedTitle')}</MessageBarTitle>
                  {t('filesExplorer.upload.register.failedText', { message: row.registerError ?? '' })}
                </MessageBarBody>
                <MessageBarActions>
                  <Button
                    appearance="transparent"
                    size="small"
                    onClick={() => row.blobPath && register(row.id, row.blobPath)}
                    data-testid="explorer-register-retry"
                  >
                    {t('filesExplorer.upload.register.retry')}
                  </Button>
                </MessageBarActions>
              </GuideMessageBar>
            )}
            {row.status === 'error' && (
              <GuideMessageBar intent="error" layout="multiline">
                <MessageBarBody>
                  <MessageBarTitle>{t('azureFileUpload.messages.uploadErrorTitle')}</MessageBarTitle>
                  {row.error}
                </MessageBarBody>
                <MessageBarActions>
                  <Button appearance="transparent" size="small" onClick={() => run([row])} disabled={running}>
                    {t('filesExplorer.actions.retry')}
                  </Button>
                </MessageBarActions>
              </GuideMessageBar>
            )}
          </li>
        ))}
      </ul>
    </GuideDrawer>
  );
};

export default ExplorerUploadPanel;
